import React, { useState, useEffect, useCallback } from "react"
import Typography from "aldoo-ra/Typography"
import { APIKeysPlatformAPI } from "../api-keys-api"
import { twMerge } from "tailwind-merge"
import { 
  KeyIcon, 
  ExclamationTriangleIcon, 
  UserGroupIcon,
  ChartBarIcon 
} from "@heroicons/react/24/outline"

// KPI Card Component with consistent styling
const KPICard = ({ title, value, icon, valueColor, iconBgColor, iconColor }) => (
  <div className="bg-admin_paper rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
    <div>
      <Typography
          component="h2"
          variant="admin_h4"
          className={twMerge("font-medium leading-none")}
        >
        {title}
      </Typography>

      <div className="flex items-center justify-between mt-6">
        <Typography 
          component="output"
          variant="admin_h2" 
          className={`${valueColor} mt-0`}
        >
          {value}
        </Typography>

        <div className={`${iconBgColor} rounded-lg p-3`}>
          {React.cloneElement(icon, { className: `w-6 h-6 ${iconColor}` })}
        </div>
      </div>
    </div>
  </div>
)

const APIKeyStatsTab = ({ refreshKey, onRefresh, onError, loading, setLoading }) => {
  const [stats, setStats] = useState(null)

  // Load statistics
  const loadStats = useCallback(async () => {
    try {
      setLoading(true)
      const response = await APIKeysPlatformAPI.GetStats()

      if (response.result && response.stats) {
        setStats(response.stats)
      } else {
        onError(response.error || "Failed to load API keys statistics")
      }
    } catch (error) {
      onError(error.message || "Failed to load API keys statistics")
    } finally {
      setLoading(false)
    }
  }, [onError, setLoading])

  // Load data when component mounts or dependencies change
  useEffect(() => {
    loadStats()
  }, [loadStats, refreshKey])

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'text-admin_success'
      case 'expired':
        return 'text-admin_warning'
      case 'revoked':
        return 'text-admin_error'
      case 'disabled':
        return 'text-admin_neutral_3'
      default:
        return 'text-admin_neutral_3'
    }
  }

  // Calculate percentage
  const getPercentage = (value, total) => {
    if (total === 0) return 0
    return Math.round((value / total) * 100)
  }

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-admin_primary"></div>
      </div>
    )
  }

  if (!stats) {
    return (
      <div className="text-center py-8">
        <Typography 
          variant="admin_body_dim" 
          className="my-0"
        >
          Failed to load statistics
        </Typography>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Key Metrics Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Total Keys */}
        <KPICard
          title="Total API Keys"
          value={stats.total}
          valueColor="text-admin_warning"
          iconBgColor="bg-admin_canvas"
          iconColor="text-admin_warning"
          icon={<KeyIcon />}
        />

        {/* Users with Keys */}
        <KPICard
          title="Users with Keys"
          value={stats.usersWithKeys}
          valueColor="text-purple-600"
          iconBgColor="bg-admin_canvas"
          iconColor="text-purple-600"
          icon={<UserGroupIcon />}
        />

        {/* Average Keys per User */}
        <KPICard
          title="Avg Keys per User"
          value={stats.avgKeysPerUser?.toFixed(1)}
          valueColor="text-admin_success"
          iconBgColor="bg-admin_canvas"
          iconColor="text-admin_success"
          icon={<ChartBarIcon />}
        />

        {/* Cache Size */}
        <KPICard
          title="Cached Keys"
          value={stats.cacheSize}
          valueColor="text-admin_error"
          iconBgColor="bg-admin_canvas"
          iconColor="text-admin_error"
          icon={<ExclamationTriangleIcon />}
        />
      </div>

      {/* Status Breakdown */}
      <div className="bg-admin_paper rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
        <Typography 
          component="h2"
          variant="admin_h4" 
          className="mb-4"
        >
          Keys by Status
        </Typography>

        <div className="space-y-3">
          {Object.entries(stats.byStatus).map(([status, count]) => {
            const percentage = getPercentage(count, stats.total)
            return (
              <div key={status}>
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${
                      status === 'active' ? 'bg-admin_success' :
                      status === 'expired' ? 'bg-admin_warning' :
                      status === 'revoked' ? 'bg-admin_error' :
                      'bg-admin_neutral_2'
                    }`}></span>

                    <Typography 
                      variant="admin_body" 
                      className="capitalize"
                    >
                      {status}
                    </Typography>
                  </div>

                  <div className="flex items-center gap-2">
                    <Typography 
                      variant="admin_body" 
                      className={getStatusColor(status)}
                    >
                      {count}
                    </Typography>

                    <Typography variant="admin_body_dim">
                      ({percentage}%)
                    </Typography>
                  </div>
                </div>
                
                {/* Progress Bar */}
                <div className="w-full bg-admin_neutral_3 rounded-full h-2">
                  <div 
                    className={`h-2 rounded-full ${
                      status === 'active' ? 'bg-admin_success' :
                      status === 'expired' ? 'bg-admin_warning' :
                      status === 'revoked' ? 'bg-admin_error' :
                      'bg-admin_neutral_2'
                    }`}
                    style={{ width: `${percentage}%` }}
                  ></div>
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Quick Insights */}
      <div className="bg-admin_paper rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
        <Typography 
          component="h2"
          variant="admin_h4" 
          className="mb-4"
        >
          Quick Insights
        </Typography>

        <div className="space-y-2">
          {stats.byStatus.expired > 0 && (
            <div className="flex items-start gap-2 bg-admin_warning/10 rounded-sm p-2">
              <ExclamationTriangleIcon 
                className="w-4 h-4 text-admin_warning flex-shrink-0 mt-0.5" 
              />

              <Typography 
                variant="admin_body" 
                className="text-admin_warning my-0"
              >
                You have {stats.byStatus.expired} expired API key{stats.byStatus.expired !== 1 ? 's' : ''} 
                that may need attention
              </Typography>
            </div>
          )}

          {stats.byStatus.active === 0 && (
            <div className="flex items-start gap-2 bg-admin_neutral_3/10 rounded-sm p-2">
              <ExclamationTriangleIcon 
                className="w-4 h-4 text-admin_neutral_3 flex-shrink-0 mt-0.5" 
              />

              <Typography variant="admin_body_dim">
                No active API keys found. Create one to enable API access.
              </Typography>
            </div>
          )}

          {stats.avgKeysPerUser > 5 && (
            <div className="flex items-start gap-2 bg-admin_error/10 rounded-sm p-2">
              <ChartBarIcon 
                className="w-4 h-4 text-admin_error flex-shrink-0 mt-0.5" 
              />

              <Typography 
                variant="admin_body" 
                className="text-admin_error"
              >
                High average keys per user ({stats.avgKeysPerUser.toFixed(1)}). 
                Consider reviewing key management practices.
              </Typography>
            </div>
          )}

          {stats.cacheSize > stats.byStatus.active && (
            <div className="flex items-start gap-2 p-2 bg-admin_warning/10 rounded">
              <KeyIcon 
                className="w-4 h-4 text-admin_warning flex-shrink-0 mt-0.5" 
              />

              <Typography 
                variant="admin_body" 
                className="text-admin_warning"
              >
                Cache contains {stats.cacheSize} keys but only {stats.byStatus.active} are active. 
                Cache cleanup may be beneficial.
              </Typography>
            </div>
          )}

          {stats.total === 0 && (
            <div className="text-center py-4">
              <KeyIcon className="w-12 h-12 text-admin_neutral_3 mx-auto mb-2" />

              <Typography 
                variant="admin_body_dim" 
                className="mb-2"
              >
                No API keys created yet
              </Typography>

              <Typography variant="admin_txt1_dim">
                Create your first API key to enable programmatic access to the system
              </Typography>
            </div>
          )}
        </div>
      </div>

      {/* Last Updated */}
      <div className="text-center">
        <Typography variant="admin_body_dim">
          Statistics last updated: {new Date().toLocaleString()}
        </Typography>
      </div>
    </div>
  )
}

export default APIKeyStatsTab